import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import apiInstance from "../../utils/axios";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import IngredientTable from "../../components/IngredientTable/IngredientTable";
import { useTranslation } from 'react-i18next';

function ProductIngredientDetails() {
  const [productId, setProductId] = useState(null);
  const [ingredients, setIngredients] = useState(null);
  const [groupedIngredients, setGroupedIngredients] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  const { hashedId } = useParams();

    let lang_browser = navigator.language.toLowerCase();
    let lang = localStorage.getItem('language') || (lang_browser.startsWith("en") ? "en" : lang_browser);

  useEffect(() => {
    if (hashedId) {
      apiInstance
        .get(`decode-id/${hashedId}/`)
        .then((res) => setProductId(res.data.id))
        .catch((error) => console.error("Error retrieving product id", error));
    }
  }, [hashedId]);


  useEffect(() => {
    if (!productId) return;

    async function fetchIngredients() {
      try {
        const res = await apiInstance.get(`/product-ingredients-detail/product_ingredients/?product=${productId}`, {headers: { "Accept-Language": `${lang}` }});
        setIngredients(res.data);
      } catch (error) {
        console.error("Error fetching ingredients:", error);
      }
    }

    fetchIngredients();
  }, [productId, lang]);


  const groupIngredients = useCallback((ingredients) => {
    const groups = {};
    ingredients.forEach((ingredient) => {
      const typeName = ingredient.ingredient.ingredient_type.name;
      if (!groups[typeName]) {
        groups[typeName] = [];
      }
      groups[typeName].push(ingredient);
    });
    return groups;
  }, []);


  useEffect(() => {
    if (ingredients) {
      setGroupedIngredients(groupIngredients(ingredients))};
  }, [ingredients, groupIngredients, lang]);

  // Function to update order
  const updateOrder = (newOrder) => {
    // Flatten grouped ingredients into a single array
    let allIngredients = [];
    Object.values(groupedIngredients).forEach(group => {
      allIngredients = allIngredients.concat(group);
    });

    // Create a map of ingredient IDs to their new order
    const orderMap = {};
    newOrder.forEach((item, index) => {
      orderMap[item.id] = index;
    });

    // Update the order in the allIngredients array
    const updatedIngredients = allIngredients.map(item => {
      if (orderMap.hasOwnProperty(item.id)) {
        return { ...item, order: orderMap[item.id] };
      }
      return item;
    }).sort((a,b)=> a.order - b.order);

    setIngredients(updatedIngredients);
    setGroupedIngredients(groupIngredients(updatedIngredients));

    apiInstance.post("/product-ingredients-detail/update-order/", {
      ingredients: updatedIngredients.map(item => ({ id: item.id, order: item.order })),
    });
  };

  // Function to update ecological_agriculture checkbox
  const updateEcological = (id, ecological) => {
    setIngredients((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ecological_agriculture: ecological } : item))
    );
    apiInstance.patch(`/product-ingredients-detail/${id}/`, {
      ecological_agriculture: ecological,
    });
  };

  return (
    <div className="container-1">
      <h1 className="m-b25">{t("productIngredientsDetails.headings.title")}</h1>
      {ingredients && (
        <IngredientTable
          ingredients={ingredients}
          updateOrder={updateOrder}
          updateEcological={updateEcological}
          ingredientsLength={ingredients.length}
        />
      )}
        
      <div className="form-input-container-3">    
        {isLoading 
        ? <button disabled className="form-button-submit-2"  >
            <i className="fas fa-spinner fa-spin"></i>
            <span className="mr-2">Processing</span>
            </button>
        : <Link className="form-button-submit-2" to="/dashboard"><span className="mr-2">Submit</span>
          </Link>
        }
      </div>
    </div>
  );
}

export default ProductIngredientDetails;